'use client'
import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Lock, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'

// Password prompt for the gated /brand-guidelines route. On submit
// POSTs to /api/brand-unlock; the route sets an httpOnly cookie and
// we refresh so the server-rendered page sees the unlock state.

export function BrandGate() {
  const [pw, setPw] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const router = useRouter()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch('/api/brand-unlock', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      })
      if (!res.ok) {
        setError(res.status === 401 ? 'Wrong password. Try again.' : 'Could not verify. Try again.')
        setSubmitting(false)
        return
      }
      router.refresh()
    } catch {
      setError('Network error. Try again.')
      setSubmitting(false)
    }
  }

  return (
    <section className="container-tight pt-20 pb-24 md:pt-28">
      <div className="mx-auto max-w-md rounded-3xl border bg-card p-8 shadow-xl">
        <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Lock className="h-5 w-5" />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">Internal · Idukki</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">
          Brand guidelines + assets library
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Logo lockups, the colour system, typography, UI primitives, slide and email-signature templates. Internal only, ask Rohin for the password if you don&rsquo;t have it.
        </p>

        <form onSubmit={submit} className="mt-7 space-y-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/75">Password</span>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl border bg-background px-3 focus-within:border-primary">
              <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden />
              <input
                type="password"
                autoComplete="off"
                spellCheck={false}
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-muted-foreground"
                placeholder="…"
                aria-label="Brand guidelines password"
              />
            </div>
          </label>
          {error && <p className="text-xs text-rose-600">{error}</p>}
          <Button type="submit" className="w-full" disabled={submitting || !pw.trim()}>
            {submitting ? 'Verifying…' : 'Unlock guidelines'}
          </Button>
        </form>

        <p className="mt-6 text-[11px] leading-relaxed text-muted-foreground">
          The lock is here so we don&rsquo;t leak unfinished asset files. Once unlocked, your browser remembers for 30 days.
        </p>
      </div>
    </section>
  )
}
